import { BuildSimplePageLocation } from './build-simple-model';
import { FieldGroupKey, TableKey, TableModel } from './extraction-models/extraction-models';
import { OcrMultiSelection } from './ocr-selection-model';
import { ExtractedValue } from './extracted-values';
import { ExtractedTableCellForm } from './extraction-form-models';
import { CtDocument, Interval, Vector2, WordRect } from '@ps-gui/claims-terminal-shared';
import {
  AuftragArt,
  ForderungspositionArt,
  Kostentraeger,
  Teilungsabkommen,
} from '@ps-gui/shared-core';
import { BehandlerListEntry } from '../pages/erfassung-behandler-page/components/model/behandler-list-entry.interface';

export const pointRect = (point: Vector2): WordRect => ({
  x: [point[0], point[0]],
  y: [point[1], point[1]],
});

export enum LineOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export const remap = (x: number, min1: number, max1: number, min2: number, max2: number) =>
  min2 + ((max2 - min2) * (x - min1)) / (max1 - min1);

export const NOT_IMPORTANT_TABLE_HEADER = 'Unwichtig';

export type PatchDto = {
  batchFields: Record<FieldGroupKey, Record<string, ExtractedValue>>;
  batchTables: Record<TableKey, Record<string, string | number | undefined>[]>;
  _301Records: object[];
  metaData?: MetaDataDto;
};

export type MetaDataDto = {
  absOrdNrDlp?: string;
};

export type AuditDataDto = {
  claimantFirstName: string;
  claimantLastName: string;
  claimantBirthDate: string;
  claimNumber: string;
  damageDate: string;
  kstName: string;
  kstStreetNr: string;
  kstZipPlace: string;
  clerkClaimAmount: number;
  businessTransaction: string;
  proofType: string;
};

export type AuditClerkOrderDto = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
};

export type AuditOrderDto = {
  tenantName: string;
  clerk: AuditClerkOrderDto;
  auditData: AuditDataDto;
  auditFiles: string[]; // TODO: wo kriegen wir die Files her?
  initialOrderDescription: string;
  shouldSendToCt: boolean;
  messageForVyda: string;
};

export type SplitEntryDto = {
  patchDto: PatchDto;
  splitIndex: number;
};

export type FullAuditOrderGuiDto = {
  orderDto: AuditOrderDto;
  eingangId?: string;
  auditId: string | null;
  splitsData: SplitEntryDto[];
};

// One created order in the /order/full-audit response. `auditRequestId` is filled only for the
// order that was actually forwarded to the ps-backend.
export type OrderResponseDto = {
  auditId: string;
  auditRequestId: string | null;
  externalAuftragId: string | null;
};

export type FullAuditOrderResponseDto = {
  orderResponses: OrderResponseDto[];
};

export type ConsolidatedDataDto = {
  betroffenePersonVorname: string;
  betroffenePersonNachname: string;
  betroffenePersonGeburtsdatum: string;
  tenant: string;
  auftragsart: string;
  schadenNummer: string;
  forderungDatum: string | null;
  schadenDatum: string;
  kennung: string | null;
  sachbearbeiterNachname: string | null;
  sachbearbeiterVorname: string | null;
  sachbearbeiterEmail: string | null;
  sachbearbeiterTelefon: string | null;
  kostentraegerName: string | null;
  kostentraegerStrasse: string | null;
  kostentraegerHausnummer: string | null;
  kostentraegerPostleitzahl: string | null;
  kostentraegerStadt: string | null;
  kostentraegerAktenzeichen: string | null;
  externeId?: string;
  nachricht?: string;
};

export type DocumentCompletionDto = {
  documentCreationDate?: string;
  documentId: string;
  extractedData: string;
  assignedBehandlerIndex?: number;
};

export type ForderungspositionCompletionDto = {
  art: string;
  beschreibung: string;
  datumVon: string | null;
  datumBis: string | null;
  originalBetrag: number;
};

export type SplitCompletionDto = {
  splitIndex: number;
  splitDocumentId: string | null;
  forderungspositionen: ForderungspositionCompletionDto[];
};

export type CompleteEingangCt2Dto = {
  behandlers: BehandlerListEntry[];
  consolidatedData: ConsolidatedDataDto;
  dokumente: DocumentCompletionDto[];
  splits: SplitCompletionDto[];
  selectedAuditId?: string | null;
  selectedAuftragId?: string | null;
};

export type AuditZuordnungDto = {
  auftragId: string;
  auditId: string;
  auditRequestId: string | null;
};

export type Ct2CompletionResponseDto = {
  auftragIds: string[];
  auditZuordnungen: AuditZuordnungDto[];
  eseId?: string;
};

export type TransformEingangCtBatchDto = {
  eingangId: string;
  auditId: string;
  auditRequestId: string;
  claimNumber: string;
  businessTransaction: string;
  tenant: string;
  proofType: string;
  orderDescription: string | null;
  requestTimestamp?: string;
  receivedAfterSubmission?: boolean;
};

export type BuildSimpleExtractedFieldLocations = Record<
  string,
  { location: BuildSimplePageLocation; pageId: string }
>;

export type ExtractedFieldLocations = Record<string, ExtractedLocationData>;

export interface ExtractedLocationData {
  selection: OcrMultiSelection;
  pageId: string;
}

export type CellIndices = `${number}|${number}`; // row|column

export interface Intersection {
  indices: CellIndices;
  row: number;
  column: number;
  xShare: number;
  share: number;
}

/**
 * Represents a "cut" of a word belonging only partially to a table cell.
 */
export interface Cut {
  /**
   * The table cell this cuts belongs to
   */
  indices: CellIndices;

  /**
   * The cutout of the word as a relative interval (e.g. [40%, 90%])
   */
  interval: Interval;
}

export interface CurrentTableCell {
  fieldKey: string;
  cellForm: ExtractedTableCellForm;
}

/**
 * Describes the configuration of the current table data capturing view,
 * i.e., from what document and what kind of table the user is currently capturing data.
 *
 * The document is optional because a table extraction view can be displayed without being
 * associated with a document directly, e.g., in a consolidation view.
 */
export interface TableDataCapturingConfig {
  document?: CtDocument;
  tableModel: TableModel;
}

export interface SplitEntity {
  copyId: string;
  splitIndex: number;
}

export interface FallOrderDto {
  mandant: string;
  schadennummer: string;
  schadendatum: string | null;
  betroffenePerson: BetroffenePersonPatchDto;
}

export interface BetroffenePersonPatchDto {
  vorname: string | null;
  name: string | null;
  geburtsdatum: string | null;
  teilschadennummer: string | null;
  medizinischeFalldaten: null;
  haftungsquote: number | null;
  proof: ProofOrderDto;
  dokumentEingaenge: DokumentEingangOrder[];
}

export interface DokumentEingangOrder {
  id: string;
  dokumente: DokumentOrder[];
  timestamp: string;
  beauftragungsText: string;
  altfallId: string;
}

export interface DokumentOrder {
  id: string;
  name: string;
  beschreibung: string;
  objectStorageId: string;
}

export interface Sachbearbeiter {
  vorname: string | null;
  name: string | null;
  email: string | null;
  telefon: string | null;
}

export interface AuftragOrderDto {
  art: AuftragArt;
  sachbearbeiter: Sachbearbeiter;
  kostentraeger: Kostentraeger;
  altfallId: string | null;
}

export interface ProofOrderDto extends AuftragOrderDto {
  forderungsdatum: string | null;
  forderungspositionen: ForderungspositionOrderDto[];
  teilungsabkommen: Teilungsabkommen;
  nichtNachgewiesenerBetrag: number | null;
}

export interface ForderungspositionOrderDto {
  art: ForderungspositionArt;
  beschreibung: string;
  von: string | null;
  bis: string | null;
  original: number;
}

export interface TaskCtBatchCompletionDto {
  taskId: string;
  documentData: TaskDocumentEntry[];
}

export interface BefundItem {
  art?: string;
  beschreibung?: string;
}

export interface DiagnoseItem {
  icd10Code?: string;
  klartext?: string;
}

export interface BehandlungItem {
  art?: string;
  datum?: string;
  beschreibung?: string;
}

export interface ExtractedFragebogenData {
  // Page 1
  behandlungVon?: string;
  behandlungBis?: string;
  behandlungstyp?: string;
  unfallhergang?: string;
  beschwerden?: string;
  beschwerdenAufgetretenAm?: string;
  arbeitsunfaehigkeitVon?: string;
  arbeitsunfaehigkeitBis?: string;
  beruf?: string;
  kostentraeger?: string;
  kostentraegerName?: string;
  gdb?: string;
  merkzeichen?: string;
  mde?: number;
  pflegegrad?: string;
  // Page 2
  befunde?: BefundItem[];
  diagnosen?: DiagnoseItem[];
  behandlungen?: BehandlungItem[];
  hinzugezogenVorname?: string;
  hinzugezogenNachname?: string;
  hinzugezogenFachrichtung?: string;
  hinzugezogenStrassehausnummer?: string;
  hinzugezogenPlz?: string;
  hinzugezogenOrt?: string;
  // Page 3
  weiterhinInBehandlung?: boolean;
  weiterbehandlerEinrichtung?: string;
  weiterbehandlerVorname?: string;
  weiterbehandlerNachname?: string;
  weiterbehandlerStrassehausnummer?: string;
  weiterbehandlerPlz?: string;
  weiterbehandlerOrt?: string;
  weitereBehandlungsdauer?: string;
  datumLetzteKonsultation?: string;
  befundLetzteKonsultation?: string;
  stoerungenImHeilverlauf?: string;
  koerperlicheGeistigeEinschraenkungen?: string;
  besserungsmassnahmen?: string;
  fachrichtungZweitmeinung?: string;
  // Page 4
  bewertungsnoten?: string;
  langfristigeFolgen?: string;
  datumAbschaetzungLangfristigeFolgen?: string;
  unfallunabhaengigeDiagnosen?: DiagnoseItem[];
  bewusstseinsstoerungen?: string[];
}

export interface TaskDocumentEntry {
  documentId: string;
  extractedFragebogenData: ExtractedFragebogenData;
  assignedBehandler?: BehandlerListEntry;
}
